import { useMemo } from 'react';

import { ApiEndpoint, DashboardEndpoint, FormActionType, FormType } from '@enums';
import type { FormMetadata } from '@types';

import { RootState, useAppSelector } from '../storeHooks';

const EMPTY_ARRAY: any[] = [];
const EMPTY_OBJECT = {};

// Simple selectors (no memoization needed - primitive values or already stable)
export const useFormState = (formType: FormType) =>
  useAppSelector((state: RootState) => state.form[formType] ?? EMPTY_OBJECT);

export const useFormInit = (formType: FormType) =>
  useAppSelector((state: RootState) => state.form[formType].init ?? false);

export const useFormSaveStatus = (formType: FormType) =>
  useAppSelector((state: RootState) => state.form[formType].saveSuccess) ?? false;

export const useFormSortStatus = (formType: FormType) =>
  useAppSelector((state: RootState) => state.form[formType].sortLoading) ?? false;

export const useFormLastUpdated = (formType: FormType) =>
  useAppSelector((state: RootState) => state.form[formType].lastUpdated ?? null);

export const useFormFilter = (formType: FormType) =>
  useAppSelector((state: RootState) => state.form[formType].filter ?? EMPTY_OBJECT);

export const useFormSortedList = (formType: FormType) =>
  useAppSelector((state: RootState) => state.form[formType].sortingList ?? EMPTY_OBJECT);

export const useFormProcedure = (formType: FormType) =>
  useAppSelector((state: RootState) => state.form[formType].procedure ?? FormActionType.Idle);

// Memoized selectors (to prevent new references on each render)
export const useFormLoadingStatus = ({ formType, endpoint = ApiEndpoint.DEFAULT }: FormMetadata) =>
  useAppSelector(
    useMemo(() => (state: RootState) => state.form[formType]?.loading?.[endpoint] ?? false, [formType, endpoint]),
  );

export const useFormLoading = ({ formType }: { formType: FormType }) =>
  useAppSelector(useMemo(() => (state: RootState) => state.form[formType]?.loading ?? EMPTY_OBJECT, [formType]));

export const useFormSubmissionStatus = ({ formType, endpoint = ApiEndpoint.DEFAULT }: FormMetadata) =>
  useAppSelector(
    useMemo(() => (state: RootState) => state.form[formType]?.submitting?.[endpoint] ?? false, [formType, endpoint]),
  );

export const useFormAllList = ({ formType }: { formType: FormType }) =>
  useAppSelector(useMemo(() => (state: RootState) => state.form[formType].formList ?? EMPTY_OBJECT, [formType]));

export const useFormList = ({ formType, endpoint = ApiEndpoint.DEFAULT }: FormMetadata) =>
  useAppSelector(
    useMemo(
      () => (state: RootState) => state.form?.[formType]?.formList?.[endpoint] ?? EMPTY_ARRAY,
      [formType, endpoint],
    ),
  );

export const useFormContent = ({ formType, endpoint = ApiEndpoint.DEFAULT }: FormMetadata) =>
  useAppSelector(
    useMemo(
      () => (state: RootState) => state.form[formType].formContent[endpoint] ?? EMPTY_OBJECT,
      [formType, endpoint],
    ),
  );

export const useFormFilterCriteria = ({ formType, endpoint }: FormMetadata) =>
  useAppSelector(
    useMemo(
      () => (state: RootState) => state.form[formType].filter.filterCriteria ?? EMPTY_ARRAY,
      [formType, endpoint],
    ),
  );

export const useFormSelectedList = ({ formType, endpoint = ApiEndpoint.DEFAULT }: FormMetadata) =>
  useAppSelector(
    useMemo(
      () => (state: RootState) => state.form[formType]?.selectedIds?.[endpoint] ?? EMPTY_ARRAY,
      [formType, endpoint],
    ),
  );

export const useFormPagination = ({
  formType,
  endpoint = ApiEndpoint.DEFAULT,
}: {
  formType: FormType;
  endpoint?: ApiEndpoint | DashboardEndpoint;
}) =>
  useAppSelector(useMemo(() => (state: RootState) => state.form[formType].pagination[endpoint], [formType, endpoint]));

export const useFormError = ({ formType, endpoint = ApiEndpoint.DEFAULT }: FormMetadata) =>
  useAppSelector(
    useMemo(() => (state: RootState) => state.form[formType].error[endpoint] ?? null, [formType, endpoint]),
  );
